import { CheckIcon } from '@heroicons/react/20/solid';
import { revealStyle, useStagedReveal } from '@odo/shared-ui';
import { PART_ICONS } from './partIcons';
import { PART_TONE_CLASSES } from './partTones';
import type { VideoPart } from './types';

interface PartSlideTextProps {
    part: VideoPart;
}

const TILE_AND_TITLE_STEPS = 2;
const REVEAL_FIRST_DELAY_MS = 100;
const REVEAL_STEP_MS = 200;
const TITLE_RISE_PX = 24;
const REVEAL_TRANSITION = 'transition-all duration-700 ease-out';

export function PartSlideText({ part }: PartSlideTextProps) {
    const { title, takeaways } = part;
    const stage = useStagedReveal(
        TILE_AND_TITLE_STEPS + takeaways.length,
        REVEAL_FIRST_DELAY_MS,
        REVEAL_STEP_MS
    );
    const tone = PART_TONE_CLASSES[part.tone];
    const Icon = PART_ICONS[part.icon];

    return (
        <div className="flex flex-col items-start text-left">
            <div className="flex items-center gap-4">
                <div
                    className={`flex items-center justify-center w-11 h-11 rounded-xl flex-shrink-0 ${tone.tile} ${REVEAL_TRANSITION}`}
                    style={revealStyle(stage >= 1)}
                >
                    <Icon className={`w-5 h-5 ${tone.icon}`} />
                </div>
                <h2
                    className={`text-3xl font-bold text-gray-900 tracking-tight ${REVEAL_TRANSITION}`}
                    style={revealStyle(stage >= 2, TITLE_RISE_PX)}
                >
                    {title}
                </h2>
            </div>
            <ul className="flex flex-col gap-3 mt-6">
                {takeaways.map((takeaway, i) => (
                    <li
                        key={takeaway}
                        className={`flex items-start gap-3 ${REVEAL_TRANSITION}`}
                        style={revealStyle(stage >= TILE_AND_TITLE_STEPS + i + 1)}
                    >
                        <CheckIcon
                            aria-hidden="true"
                            className={`w-5 h-5 mt-0.5 flex-shrink-0 ${tone.check}`}
                        />
                        <span className="text-base text-gray-600 leading-relaxed">{takeaway}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}
